import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { supabase } from "../lib/supabase";
import { useBusinessAccount } from "../business-dashboard/useBusinessAccount";
import AppLogo from "../components/AppLogo";
import Footer from "../components/Footer";

/* ─────────────────────────────────────
   FONT INJECTION — DM Sans (workhorse) +
   Instrument Serif (editorial headline
   accents), matching the rest of JiraniApp.
───────────────────────────────────── */
if (!document.getElementById("__pp_fonts")) {
  const l = document.createElement("link");
  l.id  = "__pp_fonts";
  l.rel = "stylesheet";
  l.href =
    "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ─────────────────────────────────────
   TOKENS
───────────────────────────────────── */
const T = {
  green:       "#1e9254",
  greenDark:   "#166b3e",
  greenDeep:   "#0d2e1a",
  greenTint:   "#f0faf4",
  greenBd:     "#c6e8d4",
  greenGlow:   "rgba(30,146,84,0.16)",

  bg:          "#f5f6f8",
  white:       "#ffffff",
  border:      "#e5e7eb",
  borderFaint: "#f0f2f5",
  hoverBg:     "#f9fafb",

  text:        "#0d1117",
  textMid:     "#374151",
  sub:         "#6b7280",
  faint:       "#9ca3af",

  amber:       "#92400e",
  amberBg:     "#fffbeb",
  amberBd:     "#fde68a",

  red:         "#dc2626",
  redTint:     "#fef2f2",
  redBd:       "#fecaca",

  blue:        "#1d4ed8",
  blueTint:    "#eff6ff",
  blueBd:      "#bfdbfe",

  font:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  serif: "'Instrument Serif', Georgia, serif",

  r8:   "8px",
  r10:  "10px",
  r12:  "12px",
  r14:  "14px",
  r16:  "16px",
  r20:  "20px",
  rFull:"9999px",

  s1: "0 1px 3px rgba(0,0,0,0.06)",
  s2: "0 4px 16px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)",
  s3: "0 8px 32px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.05)",
  s4: "0 16px 48px rgba(0,0,0,0.11), 0 4px 12px rgba(0,0,0,0.06)",
  sGreen: "0 4px 20px rgba(30,146,84,0.22)",
};

/* ─────────────────────────────────────
   MARKETING DATA — categories & areas used
   to build the "product" rows and the
   related-searches block. Swap these for
   your real taxonomy / buildSearchUrl()
   helper when wiring search (see note at
   the bottom of this file).
───────────────────────────────────── */
const SERVICE_CATEGORIES = [
  { key: "cleaning",             label: "House Cleaning" },
  { key: "plumbing",             label: "Plumbing" },
  { key: "electronics-repair",   label: "Electronics Repair" },
  { key: "hair-beauty",          label: "Hair & Beauty" },
  { key: "home-tutoring",        label: "Home Tutoring" },
  { key: "photography",          label: "Photography" },
  { key: "electrical",           label: "Electrical Work" },
  { key: "gardening",            label: "Gardening" },
  { key: "carpentry-furniture",  label: "Carpentry & Furniture" },
  { key: "moving",               label: "Moving Services" },
  { key: "childcare",            label: "Childcare" },
  { key: "fitness",              label: "Personal Training" },
  { key: "auto-repair",          label: "Auto Repair" },
  { key: "errands-delivery",     label: "Errands & Delivery" },
  { key: "chef",                 label: "Private Chef" },
];

const KENYAN_AREAS = [
  "Westlands","Kilimani","Lavington","Karen","Kileleshwa","Runda","Parklands",
  "South B","South C","Langata","Kasarani","Ruaka","Kikuyu","Rongai",
  "Thika Road","Upperhill","Gigiri","Muthaiga","Ngong Road","CBD","Embakasi",
  "Donholm","Buruburu","Roysambu","Kahawa",
];

function buildRelatedSearches(categoryHint, city) {
  const areas = [city, ...KENYAN_AREAS].filter(Boolean);
  const terms = [];
  SERVICE_CATEGORIES.forEach(cat => {
    areas.slice(0, cat.key === categoryHint ? 10 : 3).forEach(area => {
      terms.push({ label: `${cat.label} in ${area}`, category: cat.key, area });
    });
  });
  return terms;
}

/* ─────────────────────────────────────
   DEMO DATA (profile)
───────────────────────────────────── */
const DEMO = {
  id:               "demo_001",
  full_name:        "Mary Wanjiku",
  title:            "Professional Home Cleaner & Organiser",
  bio:              "I am Mary, a dedicated home cleaning professional with over 6 years of experience serving families across Nairobi. I treat every home as if it were my own, with care, precision, and a genuine smile.\n\nI use eco-friendly products that are safe for children and pets, and I always bring my own equipment. Whether it is a quick spruce-up or a deep seasonal clean, I show up on time, every time.",
  avatar_url:       null,
  city:             "Westlands, Nairobi",
  serves:           ["Westlands", "Kilimani", "Lavington", "Karen", "Gigiri"],
  languages:        ["English", "Swahili"],
  member_since:     "March 2021",
  availability:     "available",
  response_minutes: 8,
  response_rate:    98,
  completed_jobs:   628,
  repeat_rate:      72,
  success_score:    98,
  verified:         true,
  background_checked: true,
  phone_verified:   true,
  email_verified:   true,
  avg_rating:       4.9,
  review_count:     87,
  base_price:       800,
  badge:            "Top Rated",
  category:         "cleaning",
  skills: [
    "Deep Cleaning","Home Organisation","Carpet Cleaning","Laundry & Ironing",
    "Move-In/Out Cleaning","Airbnb Turnover","Window Cleaning","Kitchen Deep Clean",
    "Eco-Friendly Products","Office Cleaning",
  ],
  services: [
    {
      id: "svc_basic",
      name: "Basic Clean",
      price: 800,
      duration: "2 to 3 hrs",
      description: "Living areas, kitchen surfaces, bathroom, and all floors. Perfect for weekly upkeep.",
      includes: ["Living room & bedrooms","Kitchen surfaces","Bathroom scrub","Floor mopping","Rubbish removal"],
    },
    {
      id: "svc_deep",
      name: "Deep Clean",
      price: 2500,
      duration: "5 to 7 hrs",
      description: "Full home deep clean including inside appliances, cupboards, and windows. Recommended every one to two months.",
      includes: ["Everything in Basic","Inside fridge & oven","Cupboards & drawers","Window cleaning","Laundry folding"],
      popular: true,
    },
    {
      id: "svc_moveout",
      name: "Move-Out Clean",
      price: 4500,
      duration: "Full day",
      description: "Landlord-inspection ready. Every surface covered, with photo documentation included.",
      includes: ["Everything in Deep","Wall spot-cleaning","Balcony & terrace","Full carpet clean","Photo documentation"],
    },
  ],
  reviews: [
    { id:"r1", reviewer:"Amina K.",  reviewer_avatar:null, rating:5, date:"Jan 2025", text:"Mary was absolutely incredible. My apartment has never looked this clean. She got into every corner, cleaned inside the fridge, and even organised my pantry.", verified:true, repeat:true },
    { id:"r2", reviewer:"David M.", reviewer_avatar:null, rating:5, date:"Dec 2024", text:"Got my full deposit back thanks to Mary. The landlord was genuinely impressed. She documented everything with photos too.", verified:true, repeat:false },
    { id:"r3", reviewer:"Grace N.", reviewer_avatar:null, rating:5, date:"Dec 2024", text:"Very professional, arrived on time and worked quietly. My house smelled amazing when she left.", verified:true, repeat:false },
    { id:"r4", reviewer:"Brian O.", reviewer_avatar:null, rating:4, date:"Nov 2024", text:"Really good work overall. She was thorough and friendly. Took a little longer than expected but quality made up for it.", verified:true, repeat:true },
    { id:"r5", reviewer:"Faith W.", reviewer_avatar:null, rating:5, date:"Nov 2024", text:"I booked Mary for a deep clean before my parents visited. She made everything spotless.", verified:true, repeat:false },
  ],
  faqs: [
    { q:"Do you bring your own cleaning supplies?",  a:"Yes, I bring all my own eco-friendly products and equipment. You only need to provide access to water and electricity." },
    { q:"Are you available on weekends?",             a:"Yes, I am available 7 days a week including public holidays. Weekend bookings should be made at least 48 hours in advance." },
    { q:"What if I am not happy with the results?",  a:"I offer a free re-clean within 24 hours if you are not satisfied." },
    { q:"Do you work in gated communities?",          a:"Absolutely. I have experience in all major Nairobi estates and can arrange visitor passes if needed." },
  ],
  hero_media: [
    { type: "image", url: "https://picsum.photos/seed/marycleaning1/1000/750" },
    { type: "image", url: "https://picsum.photos/seed/marycleaning2/1000/750" },
    { type: "video", url: "https://www.w3schools.com/html/mov_bbb.mp4", poster: "https://picsum.photos/seed/marycleaning3/1000/750" },
    { type: "image", url: "https://picsum.photos/seed/marycleaning4/1000/750" },
    { type: "image", url: "https://picsum.photos/seed/marycleaning5/1000/750" },
    { type: "image", url: "https://picsum.photos/seed/marycleaning6/1000/750" },
  ],
};

/* ─────────────────────────────────────
   UTILS
───────────────────────────────────── */
const PALETTE = ["#334155","#0369a1","#7c3aed","#92400e","#be185d","#0e7490","#065f46"];
const avatarColor = (n="") => PALETTE[((n.charCodeAt(0)||0)+(n.charCodeAt(1)||0)) % PALETTE.length];
const toInitials  = (n="") => n.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();

function fmtDate(iso) {
  if (!iso) return "TBD";
  try { return new Date(iso).toLocaleDateString("en-KE",{weekday:"long",day:"numeric",month:"long",year:"numeric"}); }
  catch { return iso; }
}
function fmtShortDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-KE",{day:"numeric",month:"short"}); }
  catch { return ""; }
}
function fmtClock(t) {
  const [h,m] = t.split(":").map(Number);
  return `${h>12?h-12:h||12}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`;
}

/* ─────────────────────────────────────
   AVATAR
───────────────────────────────────── */
function Avatar({ name="", url, size=44, ring=false }) {
  const [err, setErr] = useState(false);
  const bg = avatarColor(name);
  return (
    <div style={{
      width:size, height:size, borderRadius:"50%", flexShrink:0, overflow:"hidden",
      background:bg, display:"flex", alignItems:"center", justifyContent:"center",
      fontSize:Math.round(size*0.34), fontWeight:600, color:"#fff", fontFamily:T.font,
      boxShadow: ring ? `0 0 0 3px ${T.white}, 0 0 0 5px ${T.border}` : "none",
    }}>
      {url && !err
        ? <img src={url} alt={name} onError={()=>setErr(true)}
            style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        : <span style={{userSelect:"none"}}>{toInitials(name)}</span>
      }
    </div>
  );
}

/* ─────────────────────────────────────
   STARS
───────────────────────────────────── */
function Stars({ n=0, size=12 }) {
  return (
    <span style={{display:"inline-flex",gap:1.5}}>
      {[1,2,3,4,5].map(i=>(
        <svg key={i} width={size} height={size} viewBox="0 0 20 20"
          fill={i<=Math.round(n)?"#f59e0b":"#e5e7eb"} style={{flexShrink:0}}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
    </span>
  );
}

/* ─────────────────────────────────────
   BADGE
───────────────────────────────────── */
function Badge({ children, variant="neutral" }) {
  const map = {
    neutral: { bg:"#f3f4f6", color:T.sub,     bd:T.border   },
    green:   { bg:T.greenTint,color:T.green,  bd:T.greenBd  },
    amber:   { bg:T.amberBg,  color:T.amber,  bd:T.amberBd  },
    blue:    { bg:T.blueTint, color:T.blue,   bd:T.blueBd   },
  };
  const s = map[variant]||map.neutral;
  return (
    <span style={{
      display:"inline-flex",alignItems:"center",gap:4,
      background:s.bg,color:s.color,border:`1px solid ${s.bd}`,
      borderRadius:T.rFull,padding:"2px 9px",
      fontSize:11,fontWeight:600,fontFamily:T.font,whiteSpace:"nowrap",
    }}>{children}</span>
  );
}

/* ─────────────────────────────────────
   BUTTON
───────────────────────────────────── */
function Btn({ children, v="ghost", size="md", onClick, disabled=false, full=false }) {
  const [hov,setHov] = useState(false);
  const [press,setPress] = useState(false);
  const sz = {
    sm:{ fs:12,h:34,pad:"0 14px",r:T.r8  },
    md:{ fs:13,h:40,pad:"0 18px",r:T.r10 },
    lg:{ fs:14,h:48,pad:"0 24px",r:T.r12 },
  }[size];
  const vs = {
    primary:{ bg:hov?T.greenDark:T.green, col:"#fff",    bd:"none",                  sh:hov?T.sGreen:"0 2px 8px rgba(30,146,84,0.18)" },
    ghost:  { bg:hov?"#f3f4f6":T.white,  col:T.textMid,  bd:`1px solid ${T.border}`, sh:"none" },
    outline:{ bg:hov?T.greenTint:T.white, col:T.green,   bd:`1px solid ${T.greenBd}`,sh:"none" },
    danger: { bg:hov?T.redTint:T.white,  col:T.red,      bd:`1px solid ${T.redBd}`,  sh:"none" },
  };
  const s = vs[v]||vs.ghost;
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={()=>setHov(true)}
      onMouseLeave={()=>{setHov(false);setPress(false);}}
      onMouseDown={()=>setPress(true)}
      onMouseUp={()=>setPress(false)}
      style={{
        display:"inline-flex",alignItems:"center",justifyContent:"center",gap:7,
        height:sz.h,padding:sz.pad,borderRadius:sz.r,
        fontSize:sz.fs,fontWeight:500,fontFamily:T.font,
        background:s.bg,color:s.col,border:s.bd,boxShadow:s.sh,
        cursor:disabled?"default":"pointer",opacity:disabled?0.45:1,
        transform:press?"scale(0.97)":"scale(1)",
        transition:"all 0.15s ease-out",whiteSpace:"nowrap",outline:"none",
        minHeight:44,...(full?{width:"100%"}:{}),
      }}>{children}</button>
  );
}

/* ─────────────────────────────────────
   ICONS
───────────────────────────────────── */
const I = {
  back:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>,
  check:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>,
  checkFat: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>,
  pin:      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  cal:      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
  clock:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>,
  msg:      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  heart: (f)=><svg width="16" height="16" viewBox="0 0 24 24" fill={f?"#ef4444":"none"} stroke={f?"#ef4444":"currentColor"} strokeWidth="1.8" strokeLinecap="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  chevD:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9l6 6 6-6"/></svg>,
  chevU:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 15l-6-6-6 6"/></svg>,
  chevL:    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>,
  chevR:    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>,
  close:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>,
  verified: <svg width="16" height="16" viewBox="0 0 24 24" fill="#3b82f6"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
  brief:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
  star:     <svg width="12" height="12" viewBox="0 0 20 20" fill="#f59e0b"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
  shield:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  card:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  user:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  info:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  repeat:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>,
  mpesa:    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7h5a3.5 3.5 0 1 1 0 7H6"/></svg>,
  phone:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.09 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  userPlus: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg>,
  play:     <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>,
  search:   <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>,
};

/* ─────────────────────────────────────
   DIVIDER
───────────────────────────────────── */
function Div({ my=0 }) {
  return <div style={{height:1,background:T.border,margin:`${my}px 0`}}/>;
}

/* ─────────────────────────────────────
   EDITORIAL HEADING — eyebrow + italic
   serif title. This is the single heading
   pattern used everywhere on the page now
   (core content sections AND marketing
   rows), matching NotificationsPage.
───────────────────────────────────── */
function EditorialHeading({ eyebrow, title, sub, size="md" }) {
  const { isMobile } = useBreakpoint();
  const sizes = {
    sm: isMobile ? 16 : 18,
    md: isMobile ? 19 : 22,
  };
  return (
    <div style={{ marginBottom: sub ? 10 : 8 }}>
      {eyebrow && (
        <p style={{ margin:"0 0 3px", fontSize:10, fontWeight:700, color:T.green,
          textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:T.font }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{
        margin:0, fontFamily:T.serif, fontStyle:"italic", fontWeight:400,
        fontSize: sizes[size], color:T.text, letterSpacing:"-0.02em", lineHeight:1.15,
      }}>{title}</h2>
      {sub && <p style={{ margin:"3px 0 0", fontSize:12.5, color:T.sub, fontFamily:T.font }}>{sub}</p>}
    </div>
  );
}
/* Kept as an alias so the marketing-row call sites read clearly. */
const MarketingHeading = (props) => <EditorialHeading size="md" {...props} />;

/* ─────────────────────────────────────
   SECTION WRAPPER — standalone rounded
   card per section (Jumia/Notifications
   density: tight border, no connected-
   stack corner trickery).
───────────────────────────────────── */
function Section({ id, eyebrow, title, children }) {
  const { isMobile } = useBreakpoint();
  return (
    <div id={`sec-${id}`} style={{
      background:   T.white,
      border:       `1.5px solid ${T.border}`,
      borderRadius: T.r16,
      padding:    isMobile ? "16px 14px" : "18px 20px",
      marginBottom: isMobile ? 10 : 12,
      boxShadow: T.s1,
    }}>
      <EditorialHeading eyebrow={eyebrow} title={title} size="sm" />
      <div style={{ marginTop: 12 }}>
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   SKELETON
───────────────────────────────────── */
function Skel({ w="100%", h=16, r=8 }) {
  return <div style={{width:w,height:h,borderRadius:r,background:"#edeef0",animation:"ppPulse 1.5s ease-in-out infinite"}}/>;
}
function PageSkeleton() {
  return (
    <div style={{maxWidth:1120,margin:"0 auto",padding:"20px 20px"}}>
      <div style={{display:"flex",gap:16,marginBottom:20}}>
        <Skel w={280} h={210} r={16}/>
        <div style={{flex:1,display:"flex",flexDirection:"column",gap:10,paddingTop:4}}>
          <Skel h={24} w={220}/><Skel h={14} w={280}/><Skel h={12} w={160}/>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 300px",gap:20}}>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {[110,180,150].map((h,i)=><Skel key={i} h={h} r={16}/>)}
        </div>
        <Skel h={400} r={16}/>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   HERO MEDIA GALLERY — compact, contained
   (not full-bleed). This sits beside the
   profile summary rather than dominating
   the page: a small square main frame with
   a thumbnail strip, sized the way a pro's
   portfolio should read — a preview, not
   a product hero.
───────────────────────────────────── */
function HeroGallery({ media, proName, size = 128 }) {
  const { isMobile } = useBreakpoint();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const items = media || [];
  const current = items[active] || items[0];
  const mainSize = isMobile ? 96 : size;

  if (!items.length) {
    return (
      <div style={{
        width:mainSize, height:mainSize, borderRadius:T.r14, flexShrink:0,
        background:T.borderFaint, border:`1px dashed ${T.border}`,
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6,
      }}>
        <span style={{ color:T.faint, display:"flex" }}>{I.brief}</span>
        <span style={{ fontSize:10, color:T.faint, fontFamily:T.font, textAlign:"center", padding:"0 8px" }}>No photos yet</span>
      </div>
    );
  }

  return (
    <>
      <div style={{ display:"flex", gap:8, flexShrink:0 }}>
        <div
          onClick={() => setLightbox(active)}
          style={{
            position:"relative", width:mainSize, height:mainSize,
            borderRadius:T.r14, overflow:"hidden", cursor:"pointer",
            background:"#e5e7eb", flexShrink:0,
            border:`1px solid ${T.border}`,
          }}>
          <img src={current.poster || current.url} alt={proName}
            style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          {current.type === "video" && (
            <div style={{
              position:"absolute", inset:0, display:"flex",
              alignItems:"center", justifyContent:"center",
              background:"rgba(13,17,23,0.22)",
            }}>
              <div style={{
                width:28, height:28, borderRadius:"50%", background:"rgba(255,255,255,0.92)",
                display:"flex", alignItems:"center", justifyContent:"center", color:T.green,
              }}><span style={{transform:"scale(0.55)",display:"flex"}}>{I.play}</span></div>
            </div>
          )}
          <div style={{
            position:"absolute", bottom:5, right:5,
            background:"rgba(13,17,23,0.65)", color:"#fff",
            fontSize:9.5, fontWeight:600, fontFamily:T.font,
            borderRadius:T.rFull, padding:"1.5px 7px",
          }}>{active+1}/{items.length}</div>
        </div>

        {!isMobile && (
          <div style={{ display:"flex", flexDirection:"column", gap:6, width:44 }}>
            {items.slice(0,4).map((m, i) => (
              <button key={i} onClick={() => setActive(i)} style={{
                position:"relative", width:44, height:i===3 && items.length>4 ? 30 : 30,
                borderRadius:T.r8, overflow:"hidden", cursor:"pointer",
                border: active===i ? `2px solid ${T.green}` : `1px solid ${T.border}`,
                padding:0, background:"#e5e7eb", flexShrink:0,
              }}>
                <img src={m.poster || m.url} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                {i===3 && items.length>4 && (
                  <div style={{
                    position:"absolute", inset:0, background:"rgba(13,17,23,0.55)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:"#fff", fontSize:10, fontWeight:700, fontFamily:T.font,
                  }}>+{items.length-4}</div>
                )}
                {m.type === "video" && i!==3 && (
                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(13,17,23,0.25)" }}>
                    <span style={{ color:"#fff", display:"flex", transform:"scale(0.4)" }}>{I.play}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div onClick={() => setLightbox(null)} style={{
          position:"fixed", inset:0, zIndex:1000, background:"rgba(13,17,23,0.92)",
          display:"flex", alignItems:"center", justifyContent:"center", padding:20,
        }}>
          <button onClick={() => setLightbox(null)} style={{
            position:"absolute", top:20, right:20, width:40, height:40, borderRadius:"50%",
            background:"rgba(255,255,255,0.12)", border:"none", color:"#fff",
            display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer",
          }}>{I.close}</button>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth:760, width:"100%" }}>
            {items[lightbox].type === "video" ? (
              <video src={items[lightbox].url} controls autoPlay style={{ width:"100%", borderRadius:T.r12 }} />
            ) : (
              <img src={items[lightbox].url} alt="" style={{ width:"100%", borderRadius:T.r12 }} />
            )}
            <div style={{ display:"flex", gap:8, marginTop:12, overflowX:"auto", paddingBottom:4 }} className="jp-scroll">
              {items.map((m,i)=>(
                <button key={i} onClick={()=>setLightbox(i)} style={{
                  flexShrink:0, width:52, height:52, borderRadius:T.r8, overflow:"hidden",
                  border: lightbox===i ? `2px solid ${T.green}` : "2px solid transparent",
                  padding:0, cursor:"pointer", background:"#333",
                }}>
                  <img src={m.poster||m.url} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────
   HORIZONTAL SCROLL ROW — the Jumia-style
   "product" carousel shell. Native
   overflow-x with snap on mobile; arrow
   buttons on desktop.
───────────────────────────────────── */
function ScrollRow({ children }) {
  const ref = useRef(null);
  const { isMobile } = useBreakpoint();
  const scrollBy = (dx) => ref.current?.scrollBy({ left: dx, behavior: "smooth" });

  return (
    <div style={{ position:"relative" }}>
      {!isMobile && (
        <>
          <button onClick={() => scrollBy(-560)} aria-label="Scroll left" style={{
            position:"absolute", left:-12, top:"50%", transform:"translateY(-50%)", zIndex:2,
            width:30, height:30, borderRadius:"50%", background:T.white, border:`1px solid ${T.border}`,
            boxShadow:T.s2, display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", color:T.textMid,
          }}><span style={{transform:"scale(0.8)",display:"flex"}}>{I.chevL}</span></button>
          <button onClick={() => scrollBy(560)} aria-label="Scroll right" style={{
            position:"absolute", right:-12, top:"50%", transform:"translateY(-50%)", zIndex:2,
            width:30, height:30, borderRadius:"50%", background:T.white, border:`1px solid ${T.border}`,
            boxShadow:T.s2, display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", color:T.textMid,
          }}><span style={{transform:"scale(0.8)",display:"flex"}}>{I.chevR}</span></button>
        </>
      )}
      <div ref={ref} className="jp-scroll" style={{
        display:"flex", gap:10, overflowX:"auto", scrollSnapType:"x mandatory",
        WebkitOverflowScrolling:"touch", paddingBottom:2,
      }}>
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   PRODUCT CARD — a service listing, not
   a pro profile. Jumia-style: image-first,
   badge, price, rating, quick location tag.
   Sized down to match the tighter density
   used across the page now.
───────────────────────────────────── */
function ProductCard({ item, navigate }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={() => navigate(`/pro/${item.proId}`)}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        flexShrink:0, width:142, scrollSnapAlign:"start", cursor:"pointer",
        background:T.white, border:`1px solid ${hov?"#d1d5db":T.border}`,
        borderRadius:T.r12, overflow:"hidden",
        boxShadow: hov ? T.s2 : "none", transition:"all 0.16s ease-out",
      }}>
      <div style={{ position:"relative", width:"100%", aspectRatio:"1 / 1", background:"#e5e7eb" }}>
        {item.img ? (
          <img src={item.img} alt={item.service} style={{ width:"100%", height:"100%", objectFit:"cover" }}
            onError={e=>{ e.currentTarget.style.display="none"; }} />
        ) : (
          <div style={{
            width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center",
            background: avatarColor(item.avatarName||item.proName||""),
          }}>
            <span style={{ color:"#fff", fontSize:26, fontWeight:600, fontFamily:T.font }}>
              {toInitials(item.avatarName||item.proName||"")}
            </span>
          </div>
        )}
        {item.badge && (
          <span style={{
            position:"absolute", top:6, left:6,
            background:T.amber, color:"#fff", fontSize:8.5, fontWeight:700,
            fontFamily:T.font, letterSpacing:"0.05em", textTransform:"uppercase",
            borderRadius:T.rFull, padding:"2px 7px",
          }}>{item.badge}</span>
        )}
      </div>
      <div style={{ padding:"8px 9px 10px" }}>
        <p style={{ margin:"0 0 2px", fontSize:13.5, fontWeight:700, color:T.text, fontFamily:T.font, letterSpacing:"-0.02em" }}>
          Ksh {item.price.toLocaleString()}
        </p>
        <p style={{
          margin:"0 0 3px", fontSize:11, color:T.textMid, fontFamily:T.font, lineHeight:1.4,
          display:"-webkit-box", WebkitLineClamp:1, WebkitBoxOrient:"vertical", overflow:"hidden",
        }}>{item.service}</p>
        {item.rating != null && (
          <div style={{ display:"flex", alignItems:"center", gap:3, marginBottom:3 }}>
            <span style={{ display:"flex", transform:"scale(0.85)" }}>{I.star}</span>
            <span style={{ fontSize:10.5, fontWeight:600, color:T.text, fontFamily:T.font }}>{item.rating.toFixed(1)}</span>
            <span style={{ fontSize:10, color:T.faint, fontFamily:T.font }}>({item.reviews})</span>
          </div>
        )}
        {item.area && (
          <div style={{ display:"flex", alignItems:"center", gap:3, color:T.faint }}>
            <span style={{ display:"flex", transform:"scale(0.85)" }}>{I.pin}</span>
            <span style={{ fontSize:10, fontFamily:T.font, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.area}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   RELATED SEARCHES — long SEO-style list
   of pill links. Wire these to your real
   search route / buildSearchUrl() helper.
───────────────────────────────────── */
function RelatedSearches({ terms, navigate }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ background:T.white, border:`1.5px solid ${T.border}`, borderRadius:T.r16, padding: isMobile ? "16px 14px" : "18px 20px" }}>
      <EditorialHeading eyebrow="Keep exploring" title="Related searches" sub="Popular services near you on JiraniApp" size="sm" />
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10 }}>
        {terms.map((t, i) => (
          <button key={i}
            onClick={() => navigate(`/search?category=${encodeURIComponent(t.category)}&location=${encodeURIComponent(t.area)}`)}
            style={{
              display:"inline-flex", alignItems:"center", gap:5,
              fontSize:12, color:T.textMid, fontFamily:T.font,
              background:T.bg, border:`1px solid ${T.border}`, borderRadius:T.rFull,
              padding:"6px 11px", cursor:"pointer", transition:"all 0.13s",
            }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor=T.greenBd; e.currentTarget.style.color=T.green; e.currentTarget.style.background=T.greenTint; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor=T.border; e.currentTarget.style.color=T.textMid; e.currentTarget.style.background=T.bg; }}
          >
            <span style={{ color:T.faint, display:"flex", transform:"scale(0.85)" }}>{I.search}</span>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   VERIFY ROW
───────────────────────────────────── */
function VerifyRow({ label, ok }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:9}}>
      <div style={{
        width:18,height:18,borderRadius:"50%",flexShrink:0,
        background:ok?T.greenTint:"#f3f4f6",
        display:"flex",alignItems:"center",justifyContent:"center",
        color:ok?T.green:T.faint,
      }}>
        {ok
          ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
          : <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"   strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
        }
      </div>
      <span style={{fontSize:12.5,color:ok?T.textMid:T.faint,fontFamily:T.font,fontWeight:ok?500:400}}>{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────
   FAQ ITEM
───────────────────────────────────── */
function FaqItem({ faq }) {
  const [open,setOpen] = useState(false);
  return (
    <div style={{borderBottom:`1px solid ${T.borderFaint}`}}>
      <button onClick={()=>setOpen(o=>!o)} style={{
        width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"13px 0",background:"none",border:"none",cursor:"pointer",
        textAlign:"left",gap:16,minHeight:44,
      }}>
        <span style={{fontSize:13.5,fontWeight:500,color:T.text,fontFamily:T.font,lineHeight:1.5}}>{faq.q}</span>
        <span style={{color:T.faint,flexShrink:0,display:"flex",transition:"transform 0.2s",transform:open?"rotate(180deg)":"none"}}>{I.chevD}</span>
      </button>
      {open && <p style={{fontSize:13,color:T.sub,lineHeight:1.75,margin:"0 0 13px",fontFamily:T.font}}>{faq.a}</p>}
    </div>
  );
}

/* ─────────────────────────────────────
   SERVICE CARD
───────────────────────────────────── */
function ServiceCard({ service, selected, onSelect }) {
  const [hov,setHov] = useState(false);
  const { isMobile } = useBreakpoint();
  return (
    <div style={{position:"relative",paddingTop:service.popular?16:0}}>
      {service.popular && (
        <div style={{
          position:"absolute",top:0,left:16,zIndex:2,
          background:T.amber,color:"#fff",
          fontSize:9.5,fontWeight:700,fontFamily:T.font,letterSpacing:"0.07em",
          padding:"3px 11px",
          borderRadius:`${"6px"} ${"6px"} 0 0`,
          textTransform:"uppercase",
        }}>Most popular</div>
      )}
      <div
        onClick={()=>onSelect(service.id)}
        onMouseEnter={()=>!selected&&setHov(true)}
        onMouseLeave={()=>setHov(false)}
        style={{
          border:`1.5px solid ${selected?T.green:hov?"#9ca3af":T.border}`,
          borderRadius: service.popular ? `0 ${T.r12} ${T.r12} ${T.r12}` : T.r12,
          padding: isMobile ? "14px" : "16px 18px",
          cursor:"pointer",
          background:selected?T.greenTint:T.white,
          transition:"border-color 0.16s,box-shadow 0.16s,background 0.16s",
          boxShadow:selected?`0 0 0 3px ${T.greenTint}`:"none",
        }}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8,gap:12}}>
          <div>
            <p style={{margin:0,fontSize:14,fontWeight:600,color:T.text,fontFamily:T.font,letterSpacing:"-0.01em"}}>{service.name}</p>
            <p style={{margin:"2px 0 0",fontSize:11.5,color:T.faint,fontFamily:T.font}}>{service.duration}</p>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <p style={{margin:0,fontSize:19,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.04em",lineHeight:1}}>
              Ksh {service.price.toLocaleString()}
            </p>
            <p style={{margin:"2px 0 0",fontSize:10.5,color:T.faint,fontFamily:T.font}}>per booking</p>
          </div>
        </div>
        <p style={{fontSize:12.5,color:T.sub,margin:"0 0 11px",lineHeight:1.65,fontFamily:T.font}}>{service.description}</p>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {service.includes.map((inc,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{color:T.textMid,flexShrink:0,opacity:0.4}}>{I.check}</span>
              <span style={{fontSize:12.5,color:T.textMid,fontFamily:T.font}}>{inc}</span>
            </div>
          ))}
        </div>
        {selected && (
          <div style={{marginTop:12,paddingTop:10,borderTop:`1px solid ${T.greenBd}`,display:"flex",alignItems:"center",gap:6,color:T.green}}>
            {I.check}
            <span style={{fontSize:11.5,fontWeight:600,fontFamily:T.font}}>Selected for booking</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   REVIEW CARD
───────────────────────────────────── */
function ReviewCard({ review }) {
  const [exp,setExp] = useState(false);
  const long = review.text.length > 220;
  return (
    <div style={{padding:"16px 0",borderBottom:`1px solid ${T.borderFaint}`}}>
      <div style={{display:"flex",alignItems:"flex-start",gap:11,marginBottom:8}}>
        <Avatar name={review.reviewer} url={review.reviewer_avatar} size={32}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",marginBottom:3}}>
            <span style={{fontSize:12.5,fontWeight:600,color:T.text,fontFamily:T.font}}>{review.reviewer}</span>
            {review.verified && <Badge variant="green">Verified booking</Badge>}
            {review.repeat   && <Badge variant="neutral">Repeat client</Badge>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <Stars n={review.rating} size={10.5}/>
            <span style={{fontSize:10.5,color:T.faint,fontFamily:T.font}}>{review.date}</span>
          </div>
        </div>
      </div>
      <p style={{fontSize:13,color:T.textMid,lineHeight:1.75,margin:0,fontFamily:T.font}}>
        {exp||!long ? review.text : review.text.slice(0,220)+"..."}
      </p>
      {long && (
        <button onClick={()=>setExp(o=>!o)} style={{
          background:"none",border:"none",color:T.green,fontSize:12.5,
          fontWeight:500,cursor:"pointer",padding:"5px 0 0",
          fontFamily:T.font,display:"flex",alignItems:"center",gap:4,
        }}>
          {exp?"Show less":"Read more"} <span style={{display:"flex"}}>{exp?I.chevU:I.chevD}</span>
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────
   RATING BREAKDOWN
───────────────────────────────────── */
function RatingBreakdown({ reviews }) {
  const counts = [5,4,3,2,1].map(s=>({s,n:reviews.filter(r=>r.rating===s).length}));
  const max = Math.max(...counts.map(c=>c.n),1);
  const avg = reviews.length
    ? (reviews.reduce((a,r)=>a+r.rating,0)/reviews.length).toFixed(1)
    : null;
  return (
    <div style={{display:"flex",gap:22,alignItems:"flex-start"}}>
      <div style={{textAlign:"center",flexShrink:0}}>
        <p style={{margin:0,fontSize:38,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.07em",lineHeight:1}}>{avg||"N/A"}</p>
        <div style={{marginTop:5,display:"flex",justifyContent:"center"}}>
          <Stars n={Math.round(parseFloat(avg)||0)} size={12}/>
        </div>
        <p style={{margin:"5px 0 0",fontSize:11,color:T.faint,fontFamily:T.font}}>{reviews.length} reviews</p>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:5,paddingTop:4}}>
        {counts.map(c=>(
          <div key={c.s} style={{display:"flex",alignItems:"center",gap:7}}>
            <span style={{fontSize:11,color:T.faint,fontFamily:T.font,width:8,textAlign:"right",flexShrink:0}}>{c.s}</span>
            <span style={{display:"flex",flexShrink:0,transform:"scale(0.85)"}}>{I.star}</span>
            <div style={{flex:1,height:4,borderRadius:T.rFull,background:"#ede9e4",overflow:"hidden"}}>
              <div style={{height:"100%",width:`${Math.round((c.n/max)*100)}%`,background:"#f59e0b",borderRadius:T.rFull,transition:"width 0.4s ease"}}/>
            </div>
            <span style={{fontSize:10.5,color:T.faint,fontFamily:T.font,width:14,flexShrink:0}}>{c.n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   BUSINESS CTA PANEL
───────────────────────────────────── */
function BusinessCTAPanel({ pro, plan, account, alreadyReserved, slotsRemaining, onReserve, onMessage, reserving, reserveSuccess }) {
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const atCapacity = slotsRemaining !== null && slotsRemaining <= 0;

  return (
    <div style={{background:T.white,border:`1.5px solid ${T.border}`,borderRadius:T.r16,overflow:"hidden",boxShadow:T.s2}}>
      <div style={{
        padding:"14px 18px",
        background:"linear-gradient(135deg,#1b7a47,#1e9254)",
        color:"#fff",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
          <span style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",opacity:0.85}}>
            {plan?.label} plan
          </span>
        </div>
        <p style={{margin:0,fontSize:12.5,opacity:0.9,fontFamily:T.font}}>
          {alreadyReserved
            ? "This Pro is already on your team."
            : atCapacity
            ? "Your Pro slots are full. You can still book this Pro on-demand."
            : slotsRemaining === null
            ? "Negotiated slots available on your plan."
            : `${slotsRemaining} slot${slotsRemaining !== 1 ? "s" : ""} remaining on your plan.`
          }
        </p>
      </div>

      <div style={{padding:"16px 18px",display:"flex",flexDirection:"column",gap:9}}>

        {alreadyReserved && (
          <>
            <div style={{
              padding:"11px 13px",borderRadius:T.r10,
              background:T.greenTint,border:`1px solid ${T.greenBd}`,
              display:"flex",alignItems:"center",gap:9,marginBottom:3,
            }}>
              <span style={{color:T.green,display:"flex"}}>{I.check}</span>
              <span style={{fontSize:12.5,fontWeight:600,color:T.greenDark,fontFamily:T.font}}>
                {pro.full_name.split(" ")[0]} is on your team
              </span>
            </div>
            <Btn v="primary" size="lg" full onClick={onMessage}>
              <span style={{display:"flex"}}>{I.msg}</span>
              Message {pro.full_name.split(" ")[0]}
            </Btn>
            <Btn v="ghost" size="md" full onClick={() => navigate("/business/account?tab=bookings")}>
              Make a booking
            </Btn>
          </>
        )}

        {!alreadyReserved && !atCapacity && (
          <>
            {reserveSuccess ? (
              <div style={{
                padding:"13px",borderRadius:T.r10,
                background:T.greenTint,border:`1px solid ${T.greenBd}`,
                textAlign:"center",
              }}>
                <div style={{fontSize:20,marginBottom:5}}>
                  <span style={{color:T.green,display:"flex",justifyContent:"center"}}>{I.checkFat}</span>
                </div>
                <p style={{margin:"0 0 3px",fontSize:13.5,fontWeight:700,color:T.greenDark,fontFamily:T.font}}>
                  Reserved successfully
                </p>
                <p style={{margin:0,fontSize:11.5,color:T.green,fontFamily:T.font}}>
                  {pro.full_name.split(" ")[0]} has been added to your team.
                </p>
              </div>
            ) : (
              <Btn v="primary" size="lg" full onClick={onReserve} disabled={reserving}>
                <span style={{display:"flex"}}>{I.userPlus}</span>
                {reserving ? "Reserving..." : `Reserve ${pro.full_name.split(" ")[0]}`}
              </Btn>
            )}
            <Btn v="ghost" size="md" full onClick={onMessage}>
              <span style={{display:"flex"}}>{I.msg}</span> Message first
            </Btn>
            <div style={{paddingTop:3,borderTop:`1px solid ${T.borderFaint}`}}>
              {["Covered by your subscription","No payment required","Cancel anytime"].map((t,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:7,marginBottom:5}}>
                  <span style={{color:T.faint,flexShrink:0,display:"flex"}}>{I.check}</span>
                  <span style={{fontSize:11.5,color:T.faint,fontFamily:T.font}}>{t}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {!alreadyReserved && atCapacity && (
          <>
            <div style={{
              padding:"11px 13px",borderRadius:T.r10,
              background:T.amberBg,border:`1px solid ${T.amberBd}`,
              display:"flex",gap:9,alignItems:"flex-start",marginBottom:3,
            }}>
              <span style={{color:T.amber,flexShrink:0,display:"flex",marginTop:1}}>{I.info}</span>
              <p style={{margin:0,fontSize:11.5,color:T.amber,fontFamily:T.font,lineHeight:1.6}}>
                Your Pro slots are full. Upgrade your plan to reserve more Pros, or book this Pro on-demand at standard rates.
              </p>
            </div>
            <Btn v="primary" size="lg" full onClick={() => navigate(`/booking/new?proId=${pro.id}&type=on_demand`)}>
              Book on-demand
            </Btn>
            <Btn v="ghost" size="sm" full onClick={() => navigate("/business/account?tab=billing")}>
              Upgrade plan to reserve
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   BOOKING PANEL (desktop right col)
───────────────────────────────────── */
function BookingPanel({ pro, services, selectedId, onSelectSvc, onBook, onMessage, saved, onToggleSave }) {
  const svc = services.find(s=>s.id===selectedId) || services[0];
  const hasServices = services.length > 0;
  const stats = [
    { label:"Response",    value:`<${pro.response_minutes||15} min`, sub:"typical reply" },
    { label:"Reply rate",  value:`${pro.response_rate||95}%`,        sub:"requests answered" },
    { label:"Jobs done",   value:(pro.completed_jobs||0).toLocaleString(), sub:`${pro.success_score||95}% success` },
    { label:"Repeat",      value:`${pro.repeat_rate||60}%`,          sub:"clients rebook" },
  ];
  return (
    <div style={{background:T.white,border:`1.5px solid ${T.border}`,borderRadius:T.r16,overflow:"hidden",boxShadow:T.s2}}>
      <div style={{padding:"18px 18px 14px",borderBottom:`1px solid ${T.borderFaint}`}}>
        {hasServices ? (
          <>
            <p style={{margin:"0 0 3px",fontSize:10.5,fontWeight:600,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.08em"}}>{svc?.name}</p>
            <div style={{display:"flex",alignItems:"baseline",gap:6}}>
              <span style={{fontSize:25,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.05em",lineHeight:1}}>
                Ksh {(svc?.price||0).toLocaleString()}
              </span>
              <span style={{fontSize:12.5,color:T.faint,fontFamily:T.font}}>/ {svc?.duration}</span>
            </div>
          </>
        ) : (
          <p style={{margin:0,fontSize:13,color:T.faint,fontFamily:T.font}}>No services listed yet</p>
        )}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",padding:"13px 18px",gap:8,borderBottom:`1px solid ${T.borderFaint}`}}>
        {stats.map(st=>(
          <div key={st.label} style={{background:"#fafafa",border:`1px solid ${T.borderFaint}`,borderRadius:T.r10,padding:"10px 12px"}}>
            <p style={{margin:"0 0 3px",fontSize:9.5,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.07em"}}>{st.label}</p>
            <p style={{margin:0,fontSize:14.5,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.03em",lineHeight:1}}>{st.value}</p>
            <p style={{margin:"2px 0 0",fontSize:9.5,color:T.faint,fontFamily:T.font,lineHeight:1.3}}>{st.sub}</p>
          </div>
        ))}
      </div>
      {hasServices && (
        <div style={{padding:"12px 18px",borderBottom:`1px solid ${T.borderFaint}`}}>
          <p style={{margin:"0 0 8px",fontSize:10.5,fontWeight:600,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.08em"}}>Choose service</p>
          <div style={{display:"flex",flexDirection:"column",gap:5}}>
            {services.map(s=>{
              const sel = selectedId===s.id;
              return (
                <button key={s.id} onClick={()=>onSelectSvc(s.id)} style={{
                  display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"8px 11px",borderRadius:T.r10,
                  border:`1.5px solid ${sel?T.green:T.border}`,
                  background:sel?T.greenTint:"transparent",
                  cursor:"pointer",fontFamily:T.font,transition:"all 0.13s",
                  gap:8,minHeight:40,textAlign:"left",
                }}>
                  <span style={{fontSize:11.5,fontWeight:sel?600:400,color:sel?T.green:T.textMid}}>{s.name}</span>
                  <span style={{fontSize:12.5,fontWeight:700,color:T.text,flexShrink:0}}>Ksh {s.price.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div style={{padding:"14px 18px 16px",display:"flex",flexDirection:"column",gap:8}}>
        <Btn v="primary" size="lg" full onClick={onBook} disabled={!hasServices}>Book now</Btn>
        <div style={{display:"flex",gap:8}}>
          <Btn v="ghost" size="md" full onClick={onMessage}>
            <span style={{display:"flex"}}>{I.msg}</span> Message
          </Btn>
          <button onClick={onToggleSave} title={saved?"Remove from saved":"Save pro"} style={{
            width:40,height:40,borderRadius:T.r10,flexShrink:0,
            border:`1px solid ${T.border}`,background:T.white,
            cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
            transition:"background 0.15s",color:T.sub,minHeight:40,
          }}
            onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
            onMouseLeave={e=>e.currentTarget.style.background=T.white}>
            {I.heart(saved)}
          </button>
        </div>
      </div>
      <div style={{padding:"0 18px 16px",display:"flex",flexDirection:"column",gap:5}}>
        {["Free cancellation up to 24 hrs before","Secure M-Pesa payment","Responds within the hour"].map((t,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:7}}>
            <span style={{color:T.faint,flexShrink:0,display:"flex"}}>{I.check}</span>
            <span style={{fontSize:11.5,color:T.faint,fontFamily:T.font}}>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────
   BOOKING MODAL
───────────────────────────────────── */
function BookingModal({ pro, services, defaultServiceId, onClose, onSuccess, user, businessId, billingType, locationId }) {
  const { open: openLogin } = useLoginModal();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const isSubscriptionBooking = billingType === "subscription";

  const [step,   setStep]   = useState(user ? 0 : "auth");
  const [svcId,  setSvcId]  = useState(defaultServiceId || services[0]?.id);
  const [date,   setDate]   = useState("");
  const [time,   setTime]   = useState("09:00");
  const [addr,   setAddr]   = useState(user?.address || "");
  const [notes,  setNotes]  = useState("");
  const [error,  setError]  = useState("");
  const [busy,   setBusy]   = useState(false);

  const svc      = services.find(s=>s.id===svcId) || services[0];
  // Platform fee (25%) is taken from the pro's payout, not added on top of
  // what the customer pays — customers never see this breakdown.
  const total = svc?.price || 0;
  const todayStr = new Date().toISOString().split("T")[0];
  const TIMES    = ["07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00"];

  useEffect(() => { if (user && step === "auth") setStep(0); }, [user, step]);

  const submit = async () => {
    if (!date)        { setError("Please choose a date."); return; }
    if (!addr.trim()) { setError("Please enter your address."); return; }
    if (!user?.id)    { setError("Please log in to continue."); return; }
    setError(""); setBusy(true);
    try {
      const scheduled = new Date(`${date}T${time}`).toISOString();
      const isRealUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(svc?.id || "");

      const { data: booking, error: bErr } = await supabase
        .from("job_requests")
        .insert({
          user_id:        user.id,
          pro_id:         pro.id,
          service_id:     isRealUuid ? svc.id : null,
          description:    svc?.name,
          status:         "submitted",
          scheduled_time: scheduled,
          location:       addr,
          notes,
          estimated_price: svc?.price || 0,
          payment_status: isSubscriptionBooking ? "covered_by_plan" : "unpaid",
          business_id:    businessId || null,
          billing_type:   billingType || null,
          location_id:    locationId || null,
        })
        .select()
        .single();
      if (bErr) throw bErr;

      try {
        await supabase.from("notifications").insert({
          user_id:  pro.id,
          type:     "new_booking",
          title:    "New booking request",
          body:     `${user.full_name||"A client"} requested ${svc?.name} on ${fmtShortDate(scheduled)}.`,
          metadata: { booking_id: booking.id },
          read:     false,
        });
      } catch (notifErr) {
        console.warn("Notification insert failed (non-fatal):", notifErr);
      }

      onSuccess(booking, svc);
    } catch (e) {
      console.error(e);
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width:"100%",padding:"11px 14px",borderRadius:T.r10,
    border:`1.5px solid ${T.border}`,fontSize:14,outline:"none",
    fontFamily:T.font,color:T.text,background:T.white,
    boxSizing:"border-box",minHeight:44,transition:"border-color 0.15s",
  };

  return (
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:900,background:"rgba(13,17,23,0.52)",backdropFilter:"blur(4px)"}}/>
      <div style={{
        position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
        zIndex:901,
        width: isMobile ? "calc(100vw - 32px)" : 480,
        maxHeight:"92vh",overflowY:"auto",
        background:T.white,borderRadius:T.r20,boxShadow:T.s4,
      }}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"20px 24px 16px",borderBottom:`1px solid ${T.borderFaint}`}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <Avatar name={pro.full_name} url={pro.avatar_url} size={36}/>
            <div>
              <p style={{margin:0,fontSize:15,fontWeight:600,color:T.text,fontFamily:T.font}}>Book {pro.full_name.split(" ")[0]}</p>
              <p style={{margin:"1px 0 0",fontSize:12,color:T.faint,fontFamily:T.font}}>{svc?.name} &middot; Ksh {(svc?.price||0).toLocaleString()}</p>
            </div>
          </div>
          <button onClick={onClose} style={{
            width:32,height:32,borderRadius:"50%",background:"#f3f4f6",
            border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:T.sub,
          }}>{I.close}</button>
        </div>

        {step === "auth" && (
          <div style={{padding:"32px 24px",textAlign:"center"}}>
            <div style={{
              width:52,height:52,borderRadius:"50%",background:T.greenTint,
              display:"flex",alignItems:"center",justifyContent:"center",
              margin:"0 auto 16px",color:T.green,
            }}>
              {I.user}
            </div>
            <p style={{margin:"0 0 8px",fontSize:17,fontWeight:600,color:T.text,fontFamily:T.font}}>Sign in to continue</p>
            <p style={{margin:"0 0 24px",fontSize:14,color:T.sub,fontFamily:T.font,lineHeight:1.6}}>
              Create a free account or sign in to book this service securely.
            </p>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <Btn v="primary" size="lg" full onClick={()=>openLogin({redirect:window.location.pathname})}>
                Sign in
              </Btn>
              <Btn v="ghost" size="lg" full onClick={()=>navigate("/signup",{state:{redirect:window.location.pathname}})}>
                Create a free account
              </Btn>
            </div>
            <p style={{margin:"16px 0 0",fontSize:12,color:T.faint,fontFamily:T.font}}>
              Your booking details are saved. You can continue right after signing in.
            </p>
          </div>
        )}

        {step === 0 && (
          <div style={{padding:"20px 24px 24px"}}>
            <p style={labelSx}>Service</p>
            <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:20}}>
              {services.map(s=>{
                const sel = svcId === s.id;
                return (
                  <button key={s.id} onClick={()=>setSvcId(s.id)} style={{
                    display:"flex",alignItems:"center",justifyContent:"space-between",
                    padding:"12px 14px",borderRadius:T.r10,
                    border:`1.5px solid ${sel?T.green:T.border}`,
                    background:sel?T.greenTint:T.white,
                    cursor:"pointer",fontFamily:T.font,transition:"all 0.13s",
                    gap:10,minHeight:44,
                  }}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{
                        width:16,height:16,borderRadius:"50%",flexShrink:0,
                        border:`2px solid ${sel?T.green:T.border}`,
                        background:sel?T.green:T.white,
                        display:"flex",alignItems:"center",justifyContent:"center",
                      }}>
                        {sel && <div style={{width:6,height:6,borderRadius:"50%",background:"#fff"}}/>}
                      </div>
                      <span style={{fontSize:13,fontWeight:500,color:T.textMid}}>{s.name}</span>
                      <span style={{fontSize:12,color:T.faint}}>{s.duration}</span>
                    </div>
                    <span style={{fontSize:14,fontWeight:700,color:T.text,flexShrink:0}}>Ksh {s.price.toLocaleString()}</span>
                  </button>
                );
              })}
            </div>

            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}>
                <span style={{color:T.faint,display:"flex"}}>{I.cal}</span>
                <p style={labelSx}>Date</p>
              </div>
              <input type="date" value={date} min={todayStr} onChange={e=>setDate(e.target.value)} style={inputStyle}/>
            </div>

            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}>
                <span style={{color:T.faint,display:"flex"}}>{I.clock}</span>
                <p style={labelSx}>Time</p>
              </div>
              <select value={time} onChange={e=>setTime(e.target.value)} style={inputStyle}>
                {TIMES.map(t=><option key={t} value={t}>{fmtClock(t)}</option>)}
              </select>
            </div>

            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}>
                <span style={{color:T.faint,display:"flex"}}>{I.pin}</span>
                <p style={labelSx}>Your address <span style={{color:T.red}}>*</span></p>
              </div>
              <input type="text" value={addr} onChange={e=>setAddr(e.target.value)}
                placeholder="e.g. Westlands, Nairobi" style={inputStyle}/>
            </div>

            <div style={{marginBottom:22}}>
              <p style={labelSx}>Notes (optional)</p>
              <textarea value={notes} onChange={e=>setNotes(e.target.value)}
                rows={3} placeholder="Gate code, access instructions, or anything the pro should know..."
                style={{...inputStyle,resize:"vertical",minHeight:80,padding:"11px 14px"}}/>
            </div>

            {error && <ErrorBox msg={error}/>}

            <Btn v="primary" size="lg" full onClick={()=>{
              if (!date)        { setError("Please choose a date."); return; }
              if (!addr.trim()) { setError("Please enter your address."); return; }
              setError(""); setStep(1);
            }}>
              Review booking
            </Btn>
          </div>
        )}

        {step === 1 && (
          <div style={{padding:"20px 24px 24px"}}>
            <p style={{margin:"0 0 18px",fontSize:14,fontWeight:600,color:T.text,fontFamily:T.font}}>Review your booking</p>

            <div style={{background:"#fafafa",borderRadius:T.r12,padding:"16px 18px",marginBottom:20,border:`1px solid ${T.borderFaint}`}}>
              {[
                { label:"Service",  value:svc?.name },
                { label:"Date",     value:fmtDate(date) },
                { label:"Time",     value:fmtClock(time) },
                { label:"Address",  value:addr },
                ...(notes ? [{ label:"Notes", value:notes }] : []),
              ].map(r=>(
                <div key={r.label} style={{display:"flex",gap:10,marginBottom:10,alignItems:"flex-start"}}>
                  <span style={{fontSize:11,fontWeight:600,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.07em",minWidth:60,flexShrink:0,paddingTop:1}}>{r.label}</span>
                  <span style={{fontSize:13,fontWeight:500,color:T.textMid,fontFamily:T.font,lineHeight:1.5}}>{r.value}</span>
                </div>
              ))}
            </div>

            <div style={{background:"#fafafa",borderRadius:T.r12,padding:"14px 18px",marginBottom:20,border:`1px solid ${T.borderFaint}`}}>
              <PriceRow label="Total to pay" val={`Ksh ${(svc?.price||0).toLocaleString()}`} bold/>
            </div>

            <div style={{
              background:T.amberBg,border:`1px solid ${T.amberBorder}`,
              borderRadius:T.r10,padding:"11px 14px",marginBottom:20,
              display:"flex",gap:9,alignItems:"flex-start",
            }}>
              <span style={{color:T.amber,flexShrink:0,display:"flex",marginTop:1}}>{I.info}</span>
              <p style={{margin:0,fontSize:12,color:T.amber,fontFamily:T.font,lineHeight:1.65}}>
                {isSubscriptionBooking
                  ? <><strong>Covered by your plan.</strong> This booking is included in your subscription — no payment is needed.</>
                  : <><strong>No payment now.</strong> You will receive an M-Pesa prompt after the pro confirms. No money leaves your account until you approve it.</>}
              </p>
            </div>

            {error && <ErrorBox msg={error}/>}

            <Btn v="primary" size="lg" full onClick={submit} disabled={busy}>
              {busy ? "Confirming..." : `Confirm booking  Ksh ${total.toLocaleString()}`}
            </Btn>

            <button onClick={()=>setStep(0)} style={{
              display:"block",margin:"12px auto 0",background:"none",border:"none",
              fontSize:13,color:T.faint,cursor:"pointer",fontFamily:T.font,
            }}>
              Edit details
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────
   MPESA MODAL
───────────────────────────────────── */
function MpesaModal({ bookingId, amount, userPhone, onSuccess, onClose }) {
  const { isMobile } = useBreakpoint();
  const [phone,   setPhone]   = useState(userPhone || "");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handlePay = async () => {
    if (!phone.trim()) { setError("Enter your M-Pesa number."); return; }
    setError("");
    setLoading(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("mpesa-initiate", {
        body: { booking_id: bookingId, phone },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);

      // Don't mark as paid here — mpesa-callback is the source of truth
      // once Safaricom confirms the transaction. onSuccess() below just
      // closes this modal; ConfirmationOverlay's "paid" state should be
      // driven by re-fetching the booking, not assumed instantly.
      onSuccess();
    } catch (err) {
      setError(err.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{position:"fixed",inset:0,zIndex:1000,background:"rgba(13,17,23,0.6)",backdropFilter:"blur(4px)"}}/>
      <div style={{
        position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
        zIndex:1001,
        width: isMobile ? "calc(100vw - 32px)" : 400,
        background:T.white,borderRadius:T.r20,boxShadow:T.s4,
        padding: isMobile ? "24px 20px" : "32px 28px",
      }}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{
            width:56,height:56,borderRadius:"50%",
            background:`linear-gradient(135deg,${T.green},#2ab86d)`,
            display:"flex",alignItems:"center",justifyContent:"center",
            margin:"0 auto 14px",boxShadow:T.sGreen,
          }}>
            <span style={{color:"#fff",display:"flex"}}>{I.mpesa}</span>
          </div>
          <h3 style={{margin:"0 0 6px",fontSize:18,fontWeight:700,color:T.text,fontFamily:T.font}}>Pay with M-Pesa</h3>
          <p style={{margin:0,fontSize:13,color:T.sub,fontFamily:T.font}}>An STK Push will be sent to your phone</p>
        </div>

        <div style={{marginBottom:16}}>
          <label style={{fontSize:11,fontWeight:600,color:T.textMid,display:"block",marginBottom:6,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.07em"}}>
            M-Pesa Phone Number
          </label>
          <div style={{position:"relative"}}>
            <span style={{
              position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",
              display:"flex",color:T.faint,
            }}>{I.phone}</span>
            <input
              value={phone}
              onChange={e=>setPhone(e.target.value)}
              placeholder="+254 712 000 000"
              style={{
                width:"100%",padding:"11px 14px 11px 36px",
                borderRadius:T.r10,
                border:`1.5px solid ${error?T.red:T.border}`,
                fontSize:14,fontFamily:T.font,outline:"none",
                boxSizing:"border-box",minHeight:44,
                transition:"border-color 0.15s",
              }}
              onFocus={e=>e.target.style.borderColor=T.green}
              onBlur={e=>e.target.style.borderColor=error?T.red:T.border}
            />
          </div>
          {error && <p style={{margin:"5px 0 0",fontSize:12,color:T.red,fontFamily:T.font}}>{error}</p>}
        </div>

        <div style={{
          background:T.greenTint,borderRadius:T.r10,
          padding:"10px 14px",marginBottom:20,
          display:"flex",justifyContent:"space-between",alignItems:"center",
          border:`1px solid ${T.greenBd}`,
        }}>
          <span style={{fontSize:13,color:T.textMid,fontFamily:T.font}}>Total to pay</span>
          <span style={{fontSize:18,fontWeight:800,color:T.text,fontFamily:T.font,letterSpacing:"-0.03em"}}>
            Ksh {amount?.toLocaleString()}
          </span>
        </div>

        <div style={{
          display:"flex",alignItems:"center",gap:7,
          marginBottom:20,padding:"10px 12px",
          background:T.amberBg,border:`1px solid ${T.amberBd}`,
          borderRadius:T.r10,
        }}>
          <span style={{color:T.amber,flexShrink:0,display:"flex"}}>{I.info}</span>
          <p style={{margin:0,fontSize:12,color:T.amber,fontFamily:T.font,lineHeight:1.6}}>
            You will receive an STK Push. Enter your M-Pesa PIN to complete payment.
          </p>
        </div>

        <div style={{display:"flex",gap:10}}>
          <Btn v="ghost" size="md" full onClick={onClose} disabled={loading}>
            Cancel
          </Btn>
          <Btn v="primary" size="md" full onClick={handlePay} disabled={loading}>
            {loading ? "Sending STK..." : `Pay Ksh ${amount?.toLocaleString()}`}
          </Btn>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────
   PAYMENT STATUS TRACKER
───────────────────────────────────── */
function PaymentStatusTracker({ paid }) {
  const steps = [
    { label:"Booking placed",    sub:"Just now",          done:true              },
    { label:"Payment received",  sub:"M-Pesa STK Push",   done:paid, active:!paid },
    { label:"Service scheduled", sub:"Awaiting pro",      done:false             },
  ];
  return (
    <div style={{
      display:"flex",alignItems:"flex-start",justifyContent:"center",
      margin:"20px 0 4px",flexWrap:"wrap",gap:0,
    }}>
      {steps.map((s,i)=>(
        <div key={i} style={{display:"flex",alignItems:"flex-start",flex:i<steps.length-1?1:"none",minWidth:90}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,minWidth:90}}>
            <div style={{
              width:32,height:32,borderRadius:"50%",
              background:s.done ? T.green : s.active ? T.white : "#f3f4f6",
              border:`2.5px solid ${s.done ? T.green : s.active ? T.green : T.border}`,
              display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:s.active ? `0 0 0 4px ${T.greenTint}` : "none",
              transition:"all 0.3s",
            }}>
              {s.done
                ? <span style={{color:"#fff",display:"flex"}}>{I.check}</span>
                : s.active
                  ? <div style={{width:10,height:10,borderRadius:"50%",background:T.green,animation:"ppPulse 1.5s ease-in-out infinite"}}/>
                  : <div style={{width:8,height:8,borderRadius:"50%",background:T.border}}/>
              }
            </div>
            <div style={{textAlign:"center"}}>
              <p style={{margin:0,fontSize:11,fontWeight:s.done||s.active?700:400,color:s.done||s.active?T.text:T.faint,fontFamily:T.font,lineHeight:1.3}}>{s.label}</p>
              <p style={{margin:"2px 0 0",fontSize:10,color:T.faint,fontFamily:T.font}}>{s.sub}</p>
            </div>
          </div>
          {i<steps.length-1 && (
            <div style={{flex:1,height:2.5,background:s.done?T.green:T.border,marginTop:14,transition:"background 0.3s"}}/>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────
   BOOKING CONFIRMATION OVERLAY
───────────────────────────────────── */
function ConfirmationOverlay({ booking, pro, service, onClose, onViewBookings, onMessage, user }) {
  const { isMobile } = useBreakpoint();
  const total = booking.estimated_price || 0; // customer-facing total — platform fee is never shown here

  const covered = booking.payment_status === "covered_by_plan";
  const [paid,      setPaid]      = useState(booking.payment_status === "paid" || covered);
  const [showMpesa, setShowMpesa] = useState(false);

  const steps = [
    { icon:I.msg,    title:`${pro.full_name.split(" ")[0]} reviews your request`, when:"Within 1 hr",    body:"They will accept or suggest a new time." },
    { icon:I.check,  title:"You receive a confirmation",                           when:"After acceptance", body:"An SMS and in-app notification will be sent once accepted." },
    { icon:I.card,   title:"Pay via M-Pesa",                                       when:"On confirmation", body:"You will receive a secure STK push. No payment before that." },
    { icon:I.user,   title:`${pro.full_name.split(" ")[0]} arrives`,               when:fmtShortDate(booking.scheduled_time)||"Scheduled date", body:"They will be at your location at the agreed time." },
  ];

  return (
    <>
      <div style={{position:"fixed",inset:0,zIndex:950,background:"rgba(13,17,23,0.6)",backdropFilter:"blur(6px)"}}/>
      <div style={{
        position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
        zIndex:951,
        width: isMobile ? "calc(100vw - 24px)" : 520,
        maxHeight:"94vh",overflowY:"auto",
        background:T.white,borderRadius:T.r20,boxShadow:T.s4,
      }}>
        <div style={{
          background:T.greenTint,borderBottom:`1px solid ${T.greenBd}`,
          padding: isMobile ? "28px 20px 24px" : "32px 28px 28px",
          textAlign:"center",
        }}>
          <div style={{
            width:64,height:64,borderRadius:"50%",
            background:`linear-gradient(135deg,${T.green},#2ab86d)`,
            display:"flex",alignItems:"center",justifyContent:"center",
            margin:"0 auto 16px",
            boxShadow:`0 0 0 12px ${T.greenTint},0 8px 32px ${T.greenGlow}`,
            animation:"cfPop 0.5s cubic-bezier(0.175,0.885,0.32,1.275) both",
            color:"#fff",
          }}>
            {I.checkFat}
          </div>
          <h2 style={{margin:"0 0 8px",fontSize:isMobile?20:22,fontWeight:700,color:T.greenDark,fontFamily:T.font,letterSpacing:"-0.03em"}}>
            {paid ? "Payment confirmed! You're all set." : "Booking request sent"}
          </h2>
          <p style={{margin:0,fontSize:14,color:T.green,fontFamily:T.font}}>
            {paid
              ? `${pro.full_name.split(" ")[0]} will start working on this right away.`
              : `${pro.full_name.split(" ")[0]} has been notified and will confirm within the hour.`
            }
          </p>
          <PaymentStatusTracker paid={paid}/>
        </div>

        <div style={{padding: isMobile ? "20px 20px" : "24px 28px"}}>
          <div style={{
            display:"flex",alignItems:"center",justifyContent:"space-between",
            background:"#fafafa",borderRadius:T.r12,padding:"12px 16px",
            marginBottom:20,border:`1px solid ${T.borderFaint}`,
            flexWrap:"wrap",gap:8,
          }}>
            <div>
              <p style={{margin:0,fontSize:10,fontWeight:700,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.08em"}}>Booking ref</p>
              <p style={{margin:"2px 0 0",fontSize:13,fontWeight:600,color:T.text,fontFamily:T.font}}>
                #{(booking.id||"").slice(0,8).toUpperCase()}
              </p>
            </div>
            {paid
              ? <span style={{display:"inline-flex",alignItems:"center",gap:5,background:T.greenTint,color:T.green,border:`1px solid ${T.greenBd}`,borderRadius:T.rFull,padding:"4px 12px",fontSize:11,fontWeight:600,fontFamily:T.font}}>
                  <span style={{width:5,height:5,borderRadius:"50%",background:T.green,flexShrink:0}}/>
                  Paid via M-Pesa
                </span>
              : <span style={{display:"inline-flex",alignItems:"center",gap:5,background:T.amberBg,color:T.amber,border:`1px solid ${T.amberBd}`,borderRadius:T.rFull,padding:"4px 12px",fontSize:11,fontWeight:600,fontFamily:T.font}}>
                  <span style={{width:5,height:5,borderRadius:"50%",background:"#f59e0b",flexShrink:0}}/>
                  Pending confirmation
                </span>
            }
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:20}}>
            {[
              { icon:I.brief, label:"Service",  value:service?.name || booking.description },
              { icon:I.cal,   label:"Date",      value:fmtDate(booking.scheduled_time) },
              { icon:I.clock, label:"Time",      value:fmtClock((booking.scheduled_time||"").replace("Z","").replace(/\+.*$/,"").split("T")[1]?.slice(0,5)||"09:00") },
              { icon:I.pin,   label:"Address",   value:booking.location },
            ].map(r=>(
              <div key={r.label} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                <div style={{width:30,height:30,borderRadius:T.r8,background:T.borderFaint,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:T.sub}}>{r.icon}</div>
                <div>
                  <p style={{margin:0,fontSize:11,fontWeight:600,color:T.faint,fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.06em"}}>{r.label}</p>
                  <p style={{margin:"3px 0 0",fontSize:14,fontWeight:500,color:T.text,fontFamily:T.font}}>{r.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{background:"#fafafa",borderRadius:T.r12,padding:"14px 16px",marginBottom:20,border:`1px solid ${T.borderFaint}`}}>
            <PriceRow label="Total" val={`Ksh ${total.toLocaleString()}`} bold/>
            <p style={{margin:"8px 0 0",fontSize:12,color:T.faint,fontFamily:T.font}}>
              {paid ? "Paid via M-Pesa" : "Payment via M-Pesa after the pro confirms."}
            </p>
          </div>

          {!paid && !covered && (
            <div style={{marginBottom:20}}>
              <Btn v="primary" size="lg" full onClick={()=>setShowMpesa(true)}>
                <span style={{display:"flex"}}>{I.mpesa}</span>
                Pay Ksh {total.toLocaleString()} with M-Pesa
              </Btn>
              <p style={{margin:"8px 0 0",textAlign:"center",fontSize:11,color:T.faint,fontFamily:T.font}}>
                You can also pay after the pro confirms.
              </p>
            </div>
          )}

          <p style={{margin:"0 0 14px",fontSize:14,fontWeight:600,color:T.text,fontFamily:T.font}}>What happens next</p>
          <div style={{position:"relative",marginBottom:24}}>
            <div style={{position:"absolute",left:15,top:28,bottom:0,width:1,background:T.borderFaint}}/>
            {steps.filter((_,i)=>!(paid&&i===2)).map((s,i,arr)=>(
              <div key={i} style={{display:"flex",gap:14,paddingBottom:i<arr.length-1?18:0,position:"relative"}}>
                <div style={{width:30,height:30,borderRadius:"50%",flexShrink:0,background:T.white,border:`1.5px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"center",color:T.faint,zIndex:1,position:"relative",boxShadow:T.s1}}>{s.icon}</div>
                <div style={{flex:1,paddingTop:4}}>
                  <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap",marginBottom:2}}>
                    <p style={{margin:0,fontSize:13,fontWeight:600,color:T.text,fontFamily:T.font}}>{s.title}</p>
                    <span style={{fontSize:10,fontWeight:600,color:T.faint,fontFamily:T.font,background:T.borderFaint,border:`1px solid ${T.border}`,borderRadius:T.rFull,padding:"1px 7px"}}>{s.when}</span>
                  </div>
                  <p style={{margin:0,fontSize:13,color:T.sub,fontFamily:T.font,lineHeight:1.6}}>{s.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <Btn v="primary" size="lg" full onClick={onViewBookings}>View my bookings</Btn>
            <div style={{display:"flex",gap:10}}>
              <Btn v="ghost" size="md" full onClick={onMessage}>
                <span style={{display:"flex"}}>{I.msg}</span> Message {pro.full_name.split(" ")[0]}
              </Btn>
              <Btn v="ghost" size="md" onClick={onClose}>Done</Btn>
            </div>
          </div>

          <p style={{margin:"14px 0 0",textAlign:"center",fontSize:12,color:T.faint,fontFamily:T.font}}>
            Free cancellation up to 24 hrs before your scheduled time.
          </p>
        </div>
      </div>

      {showMpesa && (
        <MpesaModal
          bookingId={booking.id}
          amount={total}
          userPhone={user?.phone || ""}
          onSuccess={()=>{ setPaid(true); setShowMpesa(false); }}
          onClose={()=>setShowMpesa(false)}
        />
      )}
    </>
  );
}

/* ─────────────────────────────────────
   SHARED MODAL HELPERS
───────────────────────────────────── */
const labelSx = {
  margin:"0 0 7px",fontSize:11,fontWeight:600,color:T.textMid,
  fontFamily:T.font,textTransform:"uppercase",letterSpacing:"0.07em",
  display:"block",
};

function PriceRow({ label, val, faint:isFaint, bold }) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:isFaint?5:0}}>
      <span style={{fontSize:isFaint?12:13,color:isFaint?T.faint:T.sub,fontFamily:T.font}}>{label}</span>
      <span style={{fontSize:bold?14:13,fontWeight:bold?700:500,color:bold?T.text:T.textMid,fontFamily:T.font}}>{val}</span>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{background:T.redTint,border:`1px solid ${T.redBd}`,borderRadius:T.r10,padding:"12px 14px",marginBottom:16}}>
      <p style={{margin:0,fontSize:13,color:T.red,fontFamily:T.font}}>{msg}</p>
    </div>
  );
}

/* ─────────────────────────────────────
   SKILL PILL
───────────────────────────────────── */
function SkillPill({ label }) {
  const [hov,setHov] = useState(false);
  return (
    <span onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{
      fontSize:11.5,color:hov?T.textMid:T.sub,
      border:`1px solid ${hov?"#d1d5db":T.border}`,
      background:hov?T.hoverBg:T.white,
      borderRadius:T.rFull,padding:"5px 12px",
      fontFamily:T.font,cursor:"default",transition:"all 0.13s",
    }}>{label}</span>
  );
}

function Dot() {
  return <span style={{width:3,height:3,borderRadius:"50%",background:T.border,flexShrink:0,display:"inline-block"}}/>;
}

/* ─────────────────────────────────────
   PAGE ENTRY
───────────────────────────────────── */
export default function ProProfilePage() {
  const { id }                        = useParams();
  const navigate                      = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logoutUser }          = useAuth();
  const { isMobile, isTablet }        = useBreakpoint();

  const { account, plan } = useBusinessAccount();
  const isBusinessFlow    = !!account;
  const contextParam      = searchParams.get("context");
  const isReserveContext  = contextParam === "reserve";
  const bookingTypeParam  = searchParams.get("type"); // "subscription" for reserved-pro business bookings

  const autoBook = searchParams.get("book") === "1";

  // Business bookings need a real location_id to link against for
  // per-location active-job counts (LocationsSection). Default to the
  // business's primary location — no new UI needed since is_primary
  // already exists as a real column.
  const [businessLocationId, setBusinessLocationId] = useState(null);
  useEffect(() => {
    if (!isBusinessFlow || !account?.id) return;
    (async () => {
      const { data } = await supabase
        .from("business_locations")
        .select("id")
        .eq("business_id", account.id)
        .eq("is_primary", true)
        .maybeSingle();
      setBusinessLocationId(data?.id || null);
    })();
  }, [isBusinessFlow, account?.id]);

  const [alreadyReserved, setAlreadyReserved] = useState(false);
  const [slotsRemaining,  setSlotsRemaining]  = useState(null);
  const [reserving,       setReserving]       = useState(false);
  const [reserveSuccess,  setReserveSuccess]  = useState(false);

  const [pro,            setPro]           = useState(null);
  const [loading,        setLoading]       = useState(true);
  const [selectedSvc,    setSelectedSvc]   = useState(null);
  const [saved,          setSaved]         = useState(false);
  const [showModal,      setShowModal]     = useState(false);
  const [confirmation,   setConfirmation]  = useState(null);
  const [bioExpanded,    setBioExpanded]   = useState(false);
  const [showAllReviews, setShowAllReviews]= useState(false);
  const [heroMedia,      setHeroMedia]     = useState(null);
  const [productPool,    setProductPool]   = useState([]);

  useEffect(() => {
    if (!isBusinessFlow || !account?.id || !id || id === "demo") return;
    const checkReservation = async () => {
      const { data: activeReservations, error: countErr } = await supabase
        .from("business_reserved_pros")
        .select("pro_id")
        .eq("business_id", account.id)
        .eq("status", "active");
      if (!countErr && activeReservations) {
        const alreadyOnTeam = activeReservations.some(r => r.pro_id === id);
        setAlreadyReserved(alreadyOnTeam);
        const usedSlots = activeReservations.length;
        const maxSlots  = plan?.maxPros ?? null;
        setSlotsRemaining(maxSlots === null ? null : Math.max(0, maxSlots - usedSlots));
      }
    };
    checkReservation();
  }, [isBusinessFlow, account?.id, id, plan?.maxPros]);

  const handleReservePro = async () => {
    if (!account?.id || !id) return;
    setReserving(true);
    try {
      const { error } = await supabase
        .from("business_reserved_pros")
        .insert({ business_id: account.id, pro_id: id, status: "active", reserved_at: new Date().toISOString() });
      if (error) throw error;
      await supabase.from("notifications").insert({
        recipient_id: id, icon: "UserPlus", icon_color: "#7c3aed",
        title: "New business reservation", body: `${account.name} has reserved you as part of their team.`,
        read: false, link_to: `/pro/${id}`, created_at: new Date().toISOString(),
      });
      setAlreadyReserved(true);
      setReserveSuccess(true);
      setSlotsRemaining(prev => prev === null ? null : Math.max(0, prev - 1));
    } catch (err) {
      console.error("Reservation failed:", err);
    } finally {
      setReserving(false);
    }
  };

  const fetchPro = useCallback(async () => {
    setLoading(true);
    try {
      if (!id || id === "demo") {
        setPro(DEMO); setSelectedSvc(DEMO.services[1]?.id);
        setHeroMedia(DEMO.hero_media);
        return;
      }
      const { data, error } = await supabase
        .from("pro_profiles")
        .select(`
          id, bio, title, base_price, avg_rating, review_count, completed_jobs,
          success_score, response_minutes, response_rate, repeat_rate,
          availability, verified, background_checked, phone_verified, email_verified,
          member_since, badge, category,
          profiles!user_id ( full_name, avatar_url, location ),
          pro_services!pro_id ( id, name, price, duration_minutes, description, category ),
          reviews (
            id, rating, review_text, created_at,
            reviewer:customer_id ( full_name, avatar_url )
          )
        `)
        .eq("id", id)
        .single();
      if (error) throw error;
      // Real background-check status, sourced from background_check_requests
      // (a row is created automatically at onboarding for any pro who selects
      // a childcare-related category — see ProOnboardingPage.jsx). This is
      // the source of truth for the "Background checked" badge below, NOT
      // pro_profiles.background_checked, which has no real approval process
      // behind it.
      let backgroundChecked = false;
      try {
        const { data: bgRow } = await supabase
          .from("background_check_requests")
          .select("status")
          .eq("pro_id", id)
          .maybeSingle();
        backgroundChecked = bgRow?.status === "verified";
      } catch {}

      // Best-effort portfolio / video fetch for the hero gallery.
      let media = [];
      try {
        const { data: portfolioRows } = await supabase
          .from("pro_portfolio").select("image_url").eq("pro_id", id).limit(8);
        (portfolioRows || []).forEach(r => r.image_url && media.push({ type: "image", url: r.image_url }));
      } catch {}
      try {
        const { data: videoRows } = await supabase
          .from("pro_videos").select("video_url, thumbnail_url").eq("pro_id", id).limit(4);
        (videoRows || []).forEach(r => r.video_url && media.push({ type: "video", url: r.video_url, poster: r.thumbnail_url }));
      } catch {}
      setHeroMedia(media);

      const { data: simRaw } = await supabase
        .from("pro_profiles")
        .select(`
          id, base_price, avg_rating, review_count, completed_jobs, availability, badge,
          profiles!user_id ( full_name, avatar_url, location ),
          services ( name ),
          pro_portfolio ( image_url )
        `)
        .eq("category", data.category || "cleaning")
        .neq("id", id)
        .gte("avg_rating", 4.0)
        .order("avg_rating", { ascending: false })
        .limit(18);

      // Real listings only — no fake padding. If there simply aren't
      // enough similar pros yet, the row renders shorter (or is hidden
      // below) rather than pretending there are 30.
      const realProducts = (simRaw || [])
        .filter(sp => sp.base_price != null)
        .map(sp => ({
          id:      sp.id,
          proId:   sp.id,
          proName: sp.profiles?.full_name || "JiraniPro",
          service: sp.services?.[0]?.name || "Home Services",
          price:   sp.base_price,
          rating:  sp.avg_rating || null,
          reviews: sp.review_count || 0,
          area:    sp.profiles?.location || null,
          badge:   sp.badge || null,
          img:     sp.pro_portfolio?.[0]?.image_url || null,
          avatarName: sp.profiles?.full_name || "JiraniPro",
        }));
      setProductPool(realProducts);

      const approvedServices = (data.pro_services || [])
        .filter(s => s.status === "approved" && s.is_active)
        .map(s => ({
          ...s,
          duration: s.duration_minutes ? `${s.duration_minutes} min` : null,
        }));

      const shaped = {
        ...data,
        services:     approvedServices,
        full_name:    data.profiles?.full_name  || "JiraniPro",
        avatar_url:   data.profiles?.avatar_url || null,
        city:         data.profiles?.location   || "Nairobi",
        skills:       [],
        serves:       [],
        languages:    ["English","Swahili"],
        faqs:         [],
        reviews: (data.reviews||[]).map(r=>({
          id:              r.id,
          reviewer:        r.reviewer?.full_name || "Client",
            reviewer_avatar: r.reviewer?.avatar_url || null,
            rating:          r.rating,
            date:            new Date(r.created_at).toLocaleDateString("en-KE",{month:"short",year:"numeric"}),
            text:            r.review_text || "",
            verified:        true,
            repeat:          false,
          })),
      };

      if (user?.id) {
        const { data: fav } = await supabase
          .from("favorites").select("id")
          .eq("customer_id", user.id).eq("professional_id", id).maybeSingle();
        setSaved(!!fav);
      }
      setPro(shaped);
      setSelectedSvc(shaped.services[1]?.id || shaped.services[0]?.id);
    } catch (e) {
      console.error(e);
      setPro(null);
      setHeroMedia([]);
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => { fetchPro(); window.scrollTo(0, 0); }, [fetchPro]);

  useEffect(() => {
    if (autoBook && pro && !loading) {
      setShowModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete("book");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoBook, pro, loading]);

  const handleToggleSave = useCallback(async () => {
    if (!user?.id) { setShowModal(true); return; }
    if (saved) {
      await supabase.from("favorites").delete().eq("customer_id",user.id).eq("professional_id",pro.id);
      setSaved(false);
    } else {
      await supabase.from("favorites").insert({customer_id:user.id,professional_id:pro.id});
      setSaved(true);
    }
  }, [saved, user?.id, pro?.id]);

  const handleMessage = useCallback(async () => {
    if (!user?.id) { setShowModal(true); return; }
    if (isBusinessFlow && alreadyReserved) {
      navigate(`/messages/conv_${id}`);
      return;
    }
    try {
      // Look for an existing conversation between this customer and pro —
      // conversations.customer_id / professional_id are the real columns,
      // there is no participant_ids array on this table.
      const { data: ex } = await supabase
        .from("conversations")
        .select("id")
        .eq("customer_id", user.id)
        .eq("professional_id", pro.id)
        .maybeSingle();

      if (ex?.id) { navigate(`/messages/${ex.id}`); return; }

      const { data: nc, error } = await supabase
        .from("conversations")
        .insert({
          customer_id:     user.id,
          professional_id: pro.id,
          updated_at:      new Date().toISOString(),
        })
        .select("id")
        .single();
      if (error) throw error;

      // conversation_participants is a real junction table — populate it
      // for both sides so anything relying on it (e.g. AdminMessages.jsx's
      // participant listing) works correctly.
      await supabase.from("conversation_participants").insert([
        { conversation_id: nc.id, user_id: user.id },
        { conversation_id: nc.id, user_id: pro.id },
      ]);

      navigate(`/messages/${nc.id}`);
    } catch (err) {
      console.warn("Conversation setup failed:", err);
      navigate("/messages");
    }
  }, [user?.id, pro?.id, navigate, isBusinessFlow, alreadyReserved, id]);

  const handleBookingSuccess = (booking, service) => {
    setShowModal(false);
    setConfirmation({ booking, service });
  };

  if (loading) return (
    <div id="pro-profile-page" style={{minHeight:"100vh",background:T.bg,fontFamily:T.font}}>
      <style>{CSS}</style>
      <PageSkeleton/>
    </div>
  );

  if (!pro) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:T.font}}>
      <p style={{color:T.faint}}>Professional not found.</p>
    </div>
  );

  const reviews      = pro.reviews  || [];
  const services     = pro.services || [];
  const skills       = pro.skills   || [];
  const faqs         = pro.faqs     || [];
  const serves       = pro.serves   || [];
  const bio          = pro.bio || "";
  const bioParas     = bio ? bio.split("\n\n") : [];
  const visibleRevs  = showAllReviews ? reviews : reviews.slice(0,3);
  const bodyGrid     = isMobile ? "1fr" : isTablet ? "1fr 260px" : "1fr 300px";

  // Real similar-pro listings only. Rows are hidden (not padded with
  // fake cards) when there simply isn't enough real data yet.
  const pool = productPool;
  const rowA = pool.slice(0, 12);
  const rowB = [...pool].reverse().slice(0, 12);
  const rowC = pool.slice(6, 6 + 18);
  const relatedSearches = buildRelatedSearches(pro.category, (pro.city||"Nairobi").split(",")[0]);

  const showBusinessPanel = isBusinessFlow && isReserveContext;

  return (
    <div id="pro-profile-page" style={{minHeight:"100vh",background:T.bg,fontFamily:T.font}}>
      <style>{CSS}</style>

      {/* PROFILE HERO STRIP — compact gallery + identity, side by side.
          This replaces the old full-bleed 16:9 hero: media is now a
          preview alongside the profile summary, not a takeover. */}
      <div style={{background:T.white,borderBottom:`1px solid ${T.border}`}}>
        <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "12px 14px 16px" : "16px 20px 20px"}}>

          <button onClick={()=>navigate(-1)} style={{
            display:"inline-flex",alignItems:"center",gap:6,marginBottom:12,
            background:"none",border:"none",cursor:"pointer",padding:0,
            fontSize:12.5,color:T.sub,fontFamily:T.font,
          }}
            onMouseEnter={e=>e.currentTarget.style.color=T.text}
            onMouseLeave={e=>e.currentTarget.style.color=T.sub}>
            {I.back} Back to search
          </button>

          <div style={{display:"flex",gap:isMobile?14:20,alignItems:"flex-start",flexWrap:isMobile?"wrap":"nowrap"}}>

            <HeroGallery media={heroMedia} proName={pro.full_name} size={128} />

            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                <h1 style={{margin:0,fontFamily:T.serif,fontStyle:"italic",fontWeight:400,fontSize:isMobile?21:26,color:T.text,letterSpacing:"-0.02em"}}>
                  {pro.full_name}
                </h1>
                {pro.verified && <span title="Identity verified">{I.verified}</span>}
                {pro.badge    && <Badge variant="amber">{pro.badge}</Badge>}
                {showBusinessPanel && (
                  <Badge variant="green">
                    {alreadyReserved ? "On your team" : "Business Pro"}
                  </Badge>
                )}
              </div>
              <p style={{margin:"0 0 10px",fontSize:12.5,color:T.sub,fontFamily:T.font,lineHeight:1.5}}>
                {pro.title || "Home services professional"}
              </p>
              <div style={{display:"flex",flexWrap:"wrap",gap:isMobile?7:12,alignItems:"center"}}>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <Stars n={Math.round(pro.avg_rating||0)} size={11}/>
                  {pro.avg_rating ? (
                    <>
                      <span style={{fontSize:12.5,fontWeight:600,color:T.text,fontFamily:T.font}}>
                        {pro.avg_rating.toFixed(1)}
                      </span>
                      <span style={{fontSize:12.5,color:T.faint,fontFamily:T.font}}>
                        ({pro.review_count ?? reviews.length})
                      </span>
                    </>
                  ) : (
                    <span style={{fontSize:12.5,color:T.faint,fontFamily:T.font}}>New professional</span>
                  )}
                </div>
                <Dot/>
                <div style={{display:"flex",alignItems:"center",gap:4,color:T.sub}}>
                  <span style={{display:"flex"}}>{I.pin}</span>
                  <span style={{fontSize:12.5,fontFamily:T.font}}>{pro.city || "Location not set"}</span>
                </div>
                <Dot/>
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:pro.availability==="available"?T.green:T.faint,flexShrink:0}}/>
                  <span style={{fontSize:12.5,fontWeight:500,color:pro.availability==="available"?T.green:T.faint,fontFamily:T.font}}>
                    {pro.availability==="available"?"Available now":"Busy"}
                  </span>
                </div>
                {!isMobile && (
                  <>
                    <Dot/>
                    <div style={{display:"flex",alignItems:"center",gap:4,color:T.sub}}>
                      <span style={{display:"flex"}}>{I.brief}</span>
                      <span style={{fontSize:12.5,fontFamily:T.font}}>
                        {(pro.completed_jobs||0).toLocaleString()} jobs
                      </span>
                    </div>
                  </>
                )}
              </div>
              {serves.length > 0 && (
                <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:5,alignItems:"center"}}>
                  <span style={{fontSize:11,color:T.faint,fontFamily:T.font}}>Serves</span>
                  {serves.slice(0,isMobile?3:serves.length).map(a=><Badge key={a}>{a}</Badge>)}
                </div>
              )}
            </div>

            {!isMobile && (
              <div style={{flexShrink:0,textAlign:"right"}}>
                <p style={{margin:"0 0 2px",fontSize:9.5,color:T.faint,fontFamily:T.font,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.08em"}}>Starting from</p>
                <p style={{margin:0,fontSize:24,fontWeight:700,color:T.text,fontFamily:T.font,letterSpacing:"-0.05em",lineHeight:1}}>
                  {pro.base_price ? `Ksh ${pro.base_price.toLocaleString()}` : "—"}
                </p>
                <p style={{margin:"2px 0 0",fontSize:11,color:T.faint,fontFamily:T.font}}>per booking</p>
                <div style={{marginTop:9}}>
                  <Btn v="primary" size="md" disabled={!services.length} onClick={()=>setShowModal(true)}>Book now</Btn>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PRODUCT ROW A — right after the hero, highest-intent placement.
          Only rendered when there are real similar pros to show. */}
      {rowA.length > 0 && (
        <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "16px 14px 4px" : "20px 20px 4px"}}>
          <EditorialHeading eyebrow="You might also like" title="Other pros doing this near you" sub={`Popular ${(pro.title||"home services").toLowerCase()} listings in ${(pro.city||"Nairobi").split(",")[0]}`} />
          <ScrollRow>
            {rowA.map(item => <ProductCard key={item.id} item={item} navigate={navigate} />)}
          </ScrollRow>
        </div>
      )}

      {/* BODY GRID */}
      <div style={{maxWidth:1120,margin:"0 auto",padding: isMobile ? "18px 0 32px" : "18px 20px 32px"}}>
        <div style={{display:"grid",gridTemplateColumns:bodyGrid,gap:isMobile?0:18,alignItems:"start"}}>

          <div style={{display:"flex",flexDirection:"column"}}>

            <Section id="about" eyebrow="Meet your pro" title="About">
              {bioParas.length ? (
                <>
                  {bioParas.map((p,i) =>
                    !bioExpanded && i > 0 ? null :
                    <p key={i} style={{margin:"0 0 10px",fontSize:13.5,color:T.textMid,lineHeight:1.8,fontFamily:T.font}}>{p}</p>
                  )}
                  {bioParas.length > 1 && (
                    <button onClick={()=>setBioExpanded(o=>!o)} style={{
                      background:"none",border:"none",color:T.green,fontSize:12.5,
                      fontWeight:500,cursor:"pointer",padding:0,fontFamily:T.font,
                      display:"inline-flex",alignItems:"center",gap:4,
                    }}>
                      {bioExpanded?"Show less":"Read more"}
                      <span style={{display:"flex"}}>{bioExpanded?I.chevU:I.chevD}</span>
                    </button>
                  )}
                </>
              ) : (
                <p style={{fontSize:13,color:T.faint,fontFamily:T.font}}>This professional hasn't added a bio yet.</p>
              )}
              {(pro.languages||[]).length > 0 && (
                <div style={{marginTop:13,paddingTop:12,borderTop:`1px solid ${T.borderFaint}`,display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:11.5,color:T.faint,fontFamily:T.font}}>Languages</span>
                  {pro.languages.map(l=><Badge key={l}>{l}</Badge>)}
                </div>
              )}
            </Section>

            <Section id="verification" eyebrow="Trust & safety" title="Verified by Jirani">
              <div style={{display:"flex",flexDirection:"column",gap:9}}>
                <VerifyRow label="Identity verified"  ok={!!pro.verified}/>
                <VerifyRow label="Phone verified"      ok={!!pro.phone_verified}/>
                <VerifyRow label="Email verified"      ok={!!pro.email_verified}/>
                <VerifyRow label="Background checked"  ok={!!pro.background_checked}/>
              </div>
              {pro.member_since && (
                <p style={{margin:"12px 0 0",paddingTop:12,borderTop:`1px solid ${T.borderFaint}`,fontSize:11.5,color:T.faint,fontFamily:T.font}}>
                  Member since {pro.member_since}
                </p>
              )}
            </Section>

            <Section id="services" eyebrow="Book a service" title="Services & Pricing">
              {services.length ? (
                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  {services.map(s=>(
                    <ServiceCard key={s.id} service={s} selected={selectedSvc===s.id} onSelect={setSelectedSvc}/>
                  ))}
                </div>
              ) : (
                <p style={{fontSize:13,color:T.faint,fontFamily:T.font}}>This professional hasn't listed any services yet.</p>
              )}
            </Section>

            {skills.length > 0 && (
              <Section id="skills" eyebrow="Expertise" title="Skills & Specialisms">
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {skills.map(sk=><SkillPill key={sk} label={sk}/>)}
                </div>
              </Section>
            )}

            <Section id="reviews" eyebrow="Client feedback" title="Reviews">
              {reviews.length > 0 ? (
                <>
                  <RatingBreakdown reviews={reviews}/>
                  <div style={{marginTop:18}}>
                    {visibleRevs.map(r=><ReviewCard key={r.id} review={r}/>)}
                  </div>
                  {reviews.length > 3 && (
                    <button onClick={()=>setShowAllReviews(o=>!o)} style={{
                      marginTop:14,background:"none",border:`1px solid ${T.border}`,
                      borderRadius:T.r10,padding:"9px 18px",cursor:"pointer",
                      fontSize:12.5,fontWeight:500,color:T.textMid,fontFamily:T.font,
                      transition:"background 0.13s",minHeight:40,
                    }}
                      onMouseEnter={e=>e.currentTarget.style.background=T.hoverBg}
                      onMouseLeave={e=>e.currentTarget.style.background="none"}>
                      {showAllReviews?`Show fewer reviews`:`Show all ${reviews.length} reviews`}
                    </button>
                  )}
                </>
              ) : (
                <p style={{fontSize:13,color:T.faint,fontFamily:T.font}}>No reviews yet. Be the first to book!</p>
              )}
            </Section>

            {faqs.length > 0 && (
              <Section id="faq" eyebrow="Common questions" title="Questions & Answers">
                {faqs.map((faq,i)=><FaqItem key={i} faq={faq}/>)}
              </Section>
            )}
          </div>

          {!isMobile && (
            <div style={{position:"sticky",top:16,alignSelf:"start"}}>
              {showBusinessPanel ? (
                <BusinessCTAPanel
                  pro={pro} plan={plan} account={account}
                  alreadyReserved={alreadyReserved} slotsRemaining={slotsRemaining}
                  onReserve={handleReservePro} onMessage={handleMessage}
                  reserving={reserving} reserveSuccess={reserveSuccess}
                />
              ) : (
                <BookingPanel
                  pro={pro} services={services} selectedId={selectedSvc}
                  onSelectSvc={setSelectedSvc} onBook={()=>setShowModal(true)}
                  onMessage={handleMessage} saved={saved} onToggleSave={handleToggleSave}
                />
              )}
            </div>
          )}
        </div>

        {/* PRODUCT ROW B — "frequently booked together", right after Services */}
        {rowB.length > 0 && (
          <div style={{marginTop:24}}>
            <EditorialHeading eyebrow="Frequently booked together" title="Customers also considered these" />
            <ScrollRow>
              {rowB.map(item => <ProductCard key={`b_${item.id}`} item={item} navigate={navigate} />)}
            </ScrollRow>
          </div>
        )}

        {/* PRODUCT ROW C — big browse row near the bottom, Jumia "similar products" style */}
        {rowC.length > 0 && (
          <div style={{marginTop:24}}>
            <EditorialHeading eyebrow="Similar JiraniPros" title="More home services near you" sub="Highly-rated professionals ready to book today" />
            <ScrollRow>
              {rowC.map(item => <ProductCard key={`c_${item.id}`} item={item} navigate={navigate} />)}
            </ScrollRow>
          </div>
        )}

        {/* RELATED SEARCHES */}
        <div style={{marginTop:24}}>
          <RelatedSearches terms={relatedSearches} navigate={navigate} />
        </div>
      </div>

      {/* MOBILE CTA BAR */}
      {isMobile && (
        <div style={{
          position:"fixed",bottom:0,left:0,right:0,zIndex:200,
          background:"rgba(255,255,255,0.97)",backdropFilter:"blur(12px)",
          borderTop:`1px solid ${T.border}`,padding:"10px 16px",
          display:"flex",gap:8,
        }}>
          {showBusinessPanel ? (
            <>
              <Btn v="ghost" size="md" onClick={handleMessage}>
                <span style={{display:"flex"}}>{I.msg}</span> Message
              </Btn>
              {alreadyReserved ? (
                <Btn v="ghost" size="md" full onClick={() => navigate("/business/account?tab=bookings")}>
                  Make a booking
                </Btn>
              ) : (slotsRemaining !== null && slotsRemaining <= 0) ? (
                <Btn v="primary" size="md" full onClick={() => navigate(`/booking/new?proId=${pro.id}&type=on_demand`)}>
                  Book on-demand
                </Btn>
              ) : (
                <Btn v="primary" size="md" full onClick={handleReservePro} disabled={reserving}>
                  {reserving ? "Reserving..." : `Reserve ${pro.full_name.split(" ")[0]}`}
                </Btn>
              )}
            </>
          ) : (
            <>
              <Btn v="ghost" size="md" onClick={handleMessage}>
                <span style={{display:"flex"}}>{I.msg}</span> Message
              </Btn>
              <Btn v="primary" size="md" full disabled={!services.length} onClick={()=>setShowModal(true)}>
                {services.length
                  ? `Book now · Ksh ${(services.find(s=>s.id===selectedSvc)?.price||services[0]?.price||0).toLocaleString()}`
                  : "No services listed yet"}
              </Btn>
            </>
          )}
        </div>
      )}

      {/* BOOKING MODAL — personal flow, and business on-demand/subscription flow */}
      {showModal && !showBusinessPanel && (
        <BookingModal
          pro={pro} services={services} defaultServiceId={selectedSvc}
          onClose={()=>setShowModal(false)} onSuccess={handleBookingSuccess} user={user}
          businessId={isBusinessFlow ? account?.id : null}
          billingType={isBusinessFlow ? (bookingTypeParam === "subscription" ? "subscription" : "on_demand") : null}
          locationId={isBusinessFlow ? businessLocationId : null}
        />
      )}

      {/* CONFIRMATION OVERLAY */}
      {confirmation && (
        <ConfirmationOverlay
          booking={confirmation.booking} pro={pro} service={confirmation.service}
          onClose={()=>setConfirmation(null)} onViewBookings={()=>navigate("/bookings")}
          onMessage={handleMessage} user={user}
        />
      )}

      {/* Bottom padding so the fixed mobile CTA bar never covers the footer */}
      <div style={{ height: isMobile ? 64 : 0 }} />
      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────── */
const CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  button:focus-visible { outline: 2px solid #1e9254; outline-offset: 2px; }
  input:focus, select:focus, textarea:focus {
    border-color: #1e9254 !important;
    box-shadow: 0 0 0 3px rgba(30,146,84,0.08) !important;
  }
  input::placeholder, textarea::placeholder { color: #c4c9d4; }
  input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; }
  .jp-scroll::-webkit-scrollbar { display: none; }
  .jp-scroll { scrollbar-width: none; -ms-overflow-style: none; }
  @keyframes ppPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }
  @keyframes cfPop {
    from { transform: scale(0.5); opacity: 0; }
    to   { transform: scale(1);   opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after { animation: none !important; transition: none !important; }
  }
`;